import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { cuaCloudFlag } from '@heidi/agents/flags';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';

export const DESKTOP_TARGET_SELECTOR_FLAG_KEY =
  'release-scribe-enable-rpa-desktop-target-selector';

/**
 * Users flagged into CUA Cloud get a Resident provisioned server-side; the
 * "Run by" picker must light up for them without a second flag flip, so the
 * selector reads this flag as an alternative enable.
 *
 * Cleanup sequencing: `release-agents-cua-cloud` is a temporary release flag.
 * Before it is retired, either enable the selector's own flag for the CUA
 * Cloud cohort or make the picker unconditional — otherwise Resident users
 * silently lose the picker when the flag defaults to false.
 * Kill-switch note: for the CUA Cloud cohort, turning off the selector flag
 * alone no longer hides the picker; the remote kill is disabling
 * `release-agents-cua-cloud` (which is the intended lever — it gates the
 * whole Resident feature, and a Resident without a picker is unusable).
 */
export const CUA_CLOUD_FLAG_KEY = cuaCloudFlag.key;

// Local-dev escape hatch: the dev Flagsmith environment keeps this flag off,
// so testing the cloud-computer "Run by" picker locally needs a direct
// override (set in .env.development.local, never in deployed envs).
const FORCE_ENABLED =
  process.env.NEXT_PUBLIC_FORCE_ENABLE_DESKTOP_TARGET_SELECTOR === 'true';

/**
 * FE rollout gate for the per-turn desktop-target picker. Independent of
 * `desktop_target_enabled` (BE flag frozen onto the project). Ops-toggle —
 * do not add `useManualExposure`.
 *
 * Enabled by either the selector's own flag or `release-agents-cua-cloud`:
 * a CUA Cloud user always sees the picker their Resident appears in.
 */
export const useIsDesktopTargetSelectorEnabled = (): boolean => {
  const flagValue = useBooleanFlagValue(DESKTOP_TARGET_SELECTOR_FLAG_KEY, false);
  const cuaCloudFlagValue = useBooleanFlagValue(CUA_CLOUD_FLAG_KEY, false);
  return FORCE_ENABLED || flagValue || cuaCloudFlagValue;
};

export const isDesktopTargetSelectorEnabled = (): boolean => {
  if (FORCE_ENABLED) return true;
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return (
    client.getBooleanValue(DESKTOP_TARGET_SELECTOR_FLAG_KEY, false) ||
    client.getBooleanValue(CUA_CLOUD_FLAG_KEY, false)
  );
};
